import 'reflect-metadata';

import { AxiosInstance } from 'axios';
import { createBrowserHistory } from 'history';
import React, { createContext, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router';
import routes from 'Routes';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { ConfigurationService } from 'services/ConfigurationService';
import { BallFinalFeedService } from 'services/data-services/BallFinalFeedService';
import { GamesService } from 'services/data-services/GamesService';
import { TrackingContextProvider } from 'services/helper-services/TrackingContextProvider';
import { LogService } from 'services/LogService';
import PermissionService from 'services/PermissionService';
import StartupService from 'services/StartupService';
import { DisplayDataStore } from 'stores/DisplayDataStore';
import GameStore from 'stores/GameStore';
import { LiveDashboardStore } from 'stores/LiveDashboardStore';
import { NotificationsStore } from 'stores/NotificationsStore';
import PermissionStore from 'stores/PermissionStore';
import SessionStore from 'stores/SessionStore';
import StartupStore from 'stores/StartupStore';
import UiStore from 'stores/UiStore';
import App from './App';
import { ServiceTypes } from './constants/ServiceTypes';
import './index.css';
import * as serviceWorker from './serviceWorker';

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  serviceFactory: AxiosServiceFactory;
  apiService: AxiosInstance;
  logService: LogService;
  gameApiAxiosInstance: AxiosInstance;
  gameApiService: GamesService;
  gameStore: GameStore;
  broadcastService: BallFinalFeedService;
  permissionService: PermissionService;
  permissionStore: PermissionStore;
  liveDashboardStore: LiveDashboardStore;
  sessionStore: SessionStore;
  displayDataStore: DisplayDataStore;
  notificationsStore: NotificationsStore;
  startupService: StartupService;
  startupStore: StartupStore;
  uiStore: UiStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

async function renderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiService = await serviceFactory.createInstance(ServiceTypes.ApiService);
  const logService = new LogService(apiService, new TrackingContextProvider());
  const gameApiAxiosInstance = await serviceFactory.createInstance(ServiceTypes.GamesApiService);
  const gameApiService = new GamesService(gameApiAxiosInstance, logService);
  const gameStore = new GameStore(gameApiService);
  const broadcastService = new BallFinalFeedService(logService, authenticationService);
  const permissionService = new PermissionService(apiService, logService);
  const permissionStore = new PermissionStore(permissionService);
  const liveDashboardStore = new LiveDashboardStore(permissionStore, gameStore);
  const sessionStore = new SessionStore(gameApiService, logService);
  const displayDataStore = new DisplayDataStore();
  const notificationsStore = new NotificationsStore();
  const uiStore = new UiStore();

  const startupService = new StartupService(apiService);
  const startupStore = new StartupStore(authenticationService, configurationService, startupService, notificationsStore);

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    serviceFactory,
    apiService,
    logService,
    gameApiAxiosInstance,
    gameApiService,
    gameStore,
    broadcastService,
    permissionService,
    permissionStore,
    liveDashboardStore,
    sessionStore,
    displayDataStore,
    notificationsStore,
    startupService,
    startupStore,
    uiStore
  };

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiService, authenticationService, errorHandler);
  AxiosServiceConfig.setDefaultDecorators(gameApiAxiosInstance, authenticationService, errorHandler);

  const history = createBrowserHistory();

  const root = createRoot(document.getElementById('root'));

  root.render(
    <Router history={history}>
      <StoresContext.Provider value={stores}>
        <App>{routes}</App>
      </StoresContext.Provider>
    </Router>
  );
}

renderApp();
